import AllInboxIcon from '@mui/icons-material/AllInbox';
import HandymanIcon from '@mui/icons-material/Handyman';
import InventoryIcon from '@mui/icons-material/Inventory';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Link,
  Menu,
  MenuItem,
  Snackbar,
  Switch,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { ThemeProvider, styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  checkAccessAdministrator,
  checkAccessResponsible,
  checkAccessUser,
  checkRoleFi,
} from '../../helpers/utils/RoleManager';
import useGetMatchingInternalProducts from '../../hooks/products/useMatchingInternalProducts';
import { setSnackBar } from '../../states/app/staticSlice.js';
import { setThemeColor } from '../../states/header/headerSlice';
import PandoraBrandLogo from '../svg-icons/PandoraBrandLogo.jsx';
import AlertSnackbar from './AlertSnackbar';
import LogisticDrawer from './LogisticDrawer.jsx';
import MenuHeader from './MenuHeader.jsx';
import NotificationBell from './NotificationBell.jsx';
import SearchBar from './SearchBar.jsx';
import StockVerificationDrawer from './StockVerificationDrawer.jsx';
import UserHeader from './UserHeader.jsx';

const ENV_DISPLAY = [
  {
    name: 'local',
    label: 'LOCAL',
    color: '#ff5959',
  },
  {
    name: 'development',
    label: 'DEV',
    color: '#38ac04',
  },
  {
    name: 'preproduction',
    label: 'PREPROD',
    color: '#0086ff',
  },
];

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff'
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#aab4be',
        ...theme.applyStyles('dark', {
          backgroundColor: '#8796A5',
        }),
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: '#001e3c',
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        '#fff'
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
    ...theme.applyStyles('dark', {
      backgroundColor: '#003892',
    }),
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: '#aab4be',
    borderRadius: 20 / 2,
    ...theme.applyStyles('dark', {
      backgroundColor: '#8796A5',
    }),
  },
}));

const Header = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const themeColor = useSelector((state) => state?.header?.themeColor);
  const profile = useSelector((state) => state?.login?.profile);
  const [searchIndex, setSearchIndex] = useState('');
  const [_matchingProducts, setMatchingProducts] = useState([]);
  const [_isLoading, setIsLoading] = useState(false);
  const { getMatchingProducts } = useGetMatchingInternalProducts();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [isLogisticDrawerOpen, setIsLogisticDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const snackBar = useSelector((state) => state?.static?.snackBar);

  useEffect(() => {
    if (isDrawerOpen) {
      setMatchingProducts([]);
      setSearchIndex('');
    }
  }, [isDrawerOpen]);

  const handleSwitchTheme = (themeColor) => {
    if (themeColor === 'light') {
      localStorage.setItem('theme', 'dark');
      dispatch(setThemeColor('dark'));
    } else {
      localStorage.setItem('theme', 'light');
      dispatch(setThemeColor('light'));
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
    handleClose();
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };
  const handleLogisticDrawerOpen = () => {
    setIsLogisticDrawerOpen(true);
    handleClose();
  };
  const handleLogisticDrawerClose = () => {
    setIsLogisticDrawerOpen(false);
  };

  const handleCloseSnackBar = () => {
    dispatch(setSnackBar({ open: false, message: null, color: null }));
  };
  const handleSearch = async () => {
    try {
      setIsLoading(true);
      const response = await getMatchingProducts(searchIndex);
      setMatchingProducts(response);
    } catch (error) {
      console.error('Search failed:', error);
      setMatchingProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const _handleKeyPress = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      handleSearch();
    }
  };

  if (location.pathname === '/login') {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <AlertSnackbar />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackBar.open ? true : false}
        autoHideDuration={6000}
        onClose={handleCloseSnackBar}
      >
        <Alert
          onClose={handleCloseSnackBar}
          severity={snackBar.color || 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          Message: {snackBar.message}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          display: 'flex',
          paddingTop: '64px',
          flexDirection: 'row',
        }}
      >
        <AppBar position="fixed" fontSize="small" sx={{ height: '64px' }}>
          {import.meta.env.VITE_NODE_ENV &&
            import.meta.env.VITE_NODE_ENV !== 'production' && (
              <div
                style={{
                  height: '64px',
                  width: '16px',
                  padding: 0,
                  margin: 0,
                  backgroundColor: ENV_DISPLAY.find(
                    (env) => env.name === import.meta.env.VITE_NODE_ENV
                  )?.color,
                  position: 'absolute',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    color: 'white',
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    transform: 'rotate(180deg)',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    alignSelf: 'center',
                    paddingBottom: '2px',
                  }}
                >
                  {
                    ENV_DISPLAY.find(
                      (env) => env.name === import.meta.env.VITE_NODE_ENV
                    )?.label
                  }
                </span>
              </div>
            )}
          <Toolbar
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box>
                <MenuHeader profile={profile} />
              </Box>
              <PandoraBrandLogo />
              <Typography
                component={'span'}
                variant="subtitle2"
                noWrap
                sx={{
                  marginLeft: '8px',
                  fontSize: '0.9rem',
                  color: 'rgba(255, 255, 255, 0.6)',
                }}
              >
                v{import.meta.env.VITE_PROJECT_VERSION}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
              }}
            >
              {checkAccessAdministrator(profile) ? (
                <Link
                  underline="none"
                  color="white"
                  component={RouterLink}
                  to={'/admin'}
                  sx={{
                    marginTop: 1,
                    marginLeft: 2,
                    marginBottom: 1,
                    marginRight: 2,
                  }}
                >
                  <Typography
                    component={'span'}
                    sx={{
                      fontSize: '1.4rem',
                      fontWeight: '600',
                    }}
                  >
                    Administration
                  </Typography>
                </Link>
              ) : (
                <>
                  {checkAccessUser(profile) && !checkRoleFi(profile) ? (
                    <Link
                      underline="none"
                      color="white"
                      component={RouterLink}
                      to={'/inbox'}
                      sx={{
                        marginTop: 1,
                        marginLeft: 2,
                        marginBottom: 1,
                        marginRight: 2,
                      }}
                    >
                      <Typography component={'span'} variant="h4">
                        Inbox
                      </Typography>
                    </Link>
                  ) : null}
                  {checkAccessResponsible(profile) ? (
                    <Link
                      underline="none"
                      color="white"
                      component={RouterLink}
                      to={'/dashboard-sc'}
                      sx={{
                        marginTop: 1,
                        marginLeft: 2,
                        marginBottom: 1,
                        marginRight: 2,
                      }}
                    >
                      <Typography component={'span'} variant="h4">
                        Dashboard SC
                      </Typography>
                    </Link>
                  ) : null}
                  {checkAccessResponsible(profile) ? (
                    <Link
                      underline="none"
                      color="white"
                      component={RouterLink}
                      to={'/dashboard-order'}
                      sx={{
                        marginTop: 1,
                        marginLeft: 2,
                        marginBottom: 1,
                        marginRight: 2,
                      }}
                    >
                      <Typography component={'span'} variant="h4">
                        Dashboard Order
                      </Typography>
                    </Link>
                  ) : null}
                  {/*TODO: to uncomment when dash board is ready
                   {checkAccessResponsible(profile) ? (
                    <Link
                      underline="none"
                      color="white"
                      component={RouterLink}
                      to={'/dashboard-template'}
                      sx={{
                        marginTop: 1,
                        marginLeft: 2,
                        marginBottom: 1,
                        marginRight: 2,
                      }}
                    >
                      <Typography component={'span'} variant="h4">
                        Dashboard Template
                      </Typography>
                    </Link>
                  ) : null} */}
                  {checkRoleFi(profile) ? (
                    <Link
                      underline="none"
                      color="white"
                      component={RouterLink}
                      to={'/dashboard-fi'}
                      sx={{
                        marginTop: 1,
                        marginLeft: 2,
                        marginBottom: 1,
                        marginRight: 2,
                      }}
                    >
                      <Typography component={'span'} variant="h4">
                        Dashboard FI
                      </Typography>
                    </Link>
                  ) : null}
                </>
              )}
            </Box>
            {profile && (
              <Box sx={{ flexGrow: 0.5 }}>
                <SearchBar profile={profile} />
              </Box>
            )}
            <Box>
              <MaterialUISwitch
                sx={{ flexGrow: 0 }}
                onClick={() => handleSwitchTheme(themeColor)}
                checked={themeColor === 'dark'}
              />
            </Box>
            {profile && (
              <Box sx={{ alignContent: 'right' }}>
                <Button
                  startIcon={<HandymanIcon />}
                  id="basic-button"
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleClick}
                  sx={{ color: 'white' }}
                >
                  Outils
                </Button>
                <Menu
                  id="basic-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button',
                  }}
                >
                  <MenuItem onClick={handleDrawerOpen}>
                    <AllInboxIcon sx={{ marginRight: '8px' }} />
                    <span>Vérication des stocks</span>
                  </MenuItem>
                  <MenuItem onClick={handleLogisticDrawerOpen}>
                    <InventoryIcon sx={{ marginRight: '8px' }} />
                    <span>Décompositon logistique</span>
                  </MenuItem>
                </Menu>
              </Box>
            )}
            {profile && (
              <Box sx={{ alignContent: 'right' }}>
                <NotificationBell profile={profile} />
              </Box>
            )}
            {profile && (
              <Box sx={{ alignContent: 'right' }}>
                <UserHeader profile={profile} />
              </Box>
            )}
          </Toolbar>
        </AppBar>
      </Box>
      <StockVerificationDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
      />
      <LogisticDrawer
        open={isLogisticDrawerOpen}
        onClose={handleLogisticDrawerClose}
      />
    </ThemeProvider>
  );
};

export default Header;
